import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useEffect, useState } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { LoadingSpinner } from '../../../../../../atoms/LoadingSpinner/LoadingSpinner';
import { ButtonProps, PrimaryButton } from '../../../../../../atoms/Button/Button';
import { PageView } from '../../../../../../../models/analytics';
import { useThirdPartyRedirector as useThirdPartyRedirectorImport } from '../../../../../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdImport } from '../../../../../../../stores/app-state-store';
import { CredentialsEntity } from '../../../../../../../models/credentials';
import { usePostRedirectMessage } from '../../parent-redirect-messenger';

export type ThirdPartyRedirectButtonOwnProps = {
  credentials: CredentialsEntity;
  pageView: PageView;
  onClick?: () => void;
  useThirdPartyRedirector?: typeof useThirdPartyRedirectorImport;
  showSubmitButtonInitially?: boolean;
};

export type ThirdPartyRedirectButtonProps = ButtonProps & ThirdPartyRedirectButtonOwnProps;

export const ThirdPartyRedirectButtonPresenter: React.FC<
  ThirdPartyRedirectButtonProps & PropsFromRedux
> = ({
  pageView,
  credentials,
  setRedirectedCredentialsId,
  children,
  onClick,
  useThirdPartyRedirector = useThirdPartyRedirectorImport,
  showSubmitButtonInitially = false,
  ...rest
}) => {
  const { thirdPartyRedirector } = useThirdPartyRedirector(credentials);
  usePostRedirectMessage(thirdPartyRedirector);
  const [showSubmitButton, setShowSubmitButton] = useState(showSubmitButtonInitially);

  useEffect(() => {
    if (!thirdPartyRedirector) {
      return;
    }

    if (userAgent.isMobile) {
      // We do not try to open Universal Link automatically on mobile devices.
      // If we did that, user would be redirected to a bank web page instead of bank native app. More info in SDK-584.
      setShowSubmitButton(true);
    } else {
      // Try to open thirdparty web automatically.
      // If we are redirected from TPP we should not redirect to TPP again automatically
      onClick?.();
      thirdPartyRedirector.navigate();
      setRedirectedCredentialsId(credentials.id);

      setTimeout(() => {
        setShowSubmitButton(true);
      }, 2000);
    }
  }, [
    thirdPartyRedirector,
    credentials.id,
    credentials.supplementalInformation,
    setRedirectedCredentialsId,
    onClick,
  ]);

  if (!thirdPartyRedirector || !showSubmitButton) {
    return <LoadingSpinner />;
  }

  return (
    <PrimaryButton
      type="button"
      pageView={pageView}
      onClick={() => {
        setRedirectedCredentialsId(credentials.id);
        onClick?.();
        thirdPartyRedirector.navigate();
      }}
      {...rest}
    >
      {children}
    </PrimaryButton>
  );
};

const connector = connect(undefined, {
  setRedirectedCredentialsId: setRedirectedCredentialsIdImport,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ThirdPartyRedirectButton = connector(ThirdPartyRedirectButtonPresenter);
